/**
 * MaPlace — « Ta place » : le carré du rang (or, argent, bronze pour le
 * podium), le titre (« Tu es 2e »), la phrase qui dit quoi faire, et à droite
 * ce que j'ai vendu. `outils` reçoit ce que l'écran veut poser à côté — le
 * choix Anonyme / Pseudo du classement des affiliés, par exemple.
 *
 * Reprend la section « Où j'en suis » du classement des chatteurs et des
 * affiliés ; les phrases (ex æquo, ce qu'il manque pour passer devant) restent
 * chez l'appelant, qui connaît ses règles.
 */
import { motion, useReducedMotion } from "framer-motion";
import type { ReactNode } from "react";

import { cn } from "../utils";

import "./classement.css";
import { formatAvec } from "./format";
import type { OptionsFormat } from "./types";

export interface MaPlaceProps extends OptionsFormat {
  /** `null` = pas encore classé (« ? »). */
  rang: number | null;
  titre: string;
  detail?: ReactNode;
  /** Mon chiffre, à droite ; `null` ou absent = rien. */
  montant?: number | null;
  /** Petites capitales au-dessus du titre (défaut « Ta place »). */
  etiquette?: string;
  /** Petites capitales au-dessus du montant (défaut « Vendu cette semaine »). */
  etiquetteMontant?: string;
  /** À droite : un réglage, un bouton… */
  outils?: ReactNode;
  /** Une seule ligne (titre + phrase côte à côte) pour un panneau bas. */
  compact?: boolean;
  className?: string;
}

export function MaPlace({
  rang,
  titre,
  detail,
  montant,
  etiquette = "Ta place",
  etiquetteMontant = "Vendu cette semaine",
  format = "montant",
  devise = "EUR",
  suffixe,
  formater,
  outils,
  compact = false,
  className,
}: MaPlaceProps) {
  const reduit = useReducedMotion() ?? false;
  const metal = rang !== null && rang >= 1 && rang <= 3 ? rang : null;
  return (
    <section
      className={cn(
        "cl-root cl-carte flex flex-wrap items-center",
        compact ? "gap-2.5 px-3 py-2" : "gap-3 px-4 py-3",
        className,
      )}
    >
      <motion.span
        key={rang ?? "nc"}
        className="cl-montant cl-rang-carre"
        data-metal={metal ?? undefined}
        data-compact={compact || undefined}
        initial={reduit ? false : { scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 420, damping: 20 }}
      >
        {rang === null ? "?" : `#${rang}`}
      </motion.span>
      {compact ? (
        <span className="flex min-w-0 flex-1 basis-40 flex-wrap items-baseline gap-x-2">
          <span className="cl-montant truncate text-[18px] font-semibold leading-tight text-fg">{titre}</span>
          {detail && <span className="min-w-0 truncate text-[12px] text-muted">{detail}</span>}
        </span>
      ) : (
        <span className="flex min-w-0 flex-1 basis-40 flex-col">
          <span className="cl-etiquette text-[10px] text-faint">{etiquette}</span>
          <span className="cl-montant truncate text-[22px] font-semibold leading-tight text-fg">{titre}</span>
          {detail && <span className="mt-0.5 text-[12px] text-muted">{detail}</span>}
        </span>
      )}
      {montant !== undefined && montant !== null && (
        <span className="flex shrink-0 flex-col items-end text-right">
          <span className={cn("cl-etiquette text-faint", compact ? "text-[9px]" : "text-[10px]")}>{etiquetteMontant}</span>
          <span className={cn("cl-montant font-semibold leading-tight text-fg", compact ? "text-[18px]" : "text-[22px]")}>
            {formatAvec(montant, { format, devise, formater })}
            {suffixe && <span className="text-[12px] font-normal text-muted">{suffixe}</span>}
          </span>
        </span>
      )}
      {outils && <span className="flex shrink-0 items-center">{outils}</span>}
    </section>
  );
}
